import { useQuery } from '@apollo/client';
import { faAngleRight } from '@fortawesome/pro-light-svg-icons/faAngleRight';
import { faExclamation } from '@fortawesome/free-solid-svg-icons/faExclamation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Container, FlexGrid, Header, Link, Loader, Message, Picture, Text } from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import { Fragment } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { buildIdArticlePath, getNewsroomFormattedDate } from '../../../lib/utils/newsroom';
import { SUPPORTED_LANGUAGES } from '../../constants';
import GradientBanner from '../GradientBanner';
import Page from '../Page';
import styles from './NewsroomPage.module.css';

const cx = classnames.bind(styles);

const PAGE_LENGTH = 4;
const PAGE_IMAGE = 'https://images.gasbuddy.com/images/websites/gasbuddy/apps/newsroom_app_card.jpg';

export const GET_ARTICLES = gql`
  query Articles($years: [Int!]) {
    newsArticles(years: $years) {
      date
      headline
      headlineLink
      source
    }
    pressReleases(years: $years) {
      date
      headline
      locales {
        fr {
          headline
        }
      }
      legacyId
      title
    }
  }
`;

function ArticlePreview({ article, isExternalLink }) {
  const { i18n } = useTranslation();
  if (!article) {
    return null;
  }

  const articleUrl = isExternalLink ? article.headlineLink : buildIdArticlePath(article);
  const formattedArticleDate = getNewsroomFormattedDate(article.date, i18n.language);

  const translatedHeadline = i18n.language === SUPPORTED_LANGUAGES.FRENCH && article.locales?.fr?.headline
    ? article.locales.fr.headline
    : article.headline;

  const formattedHeadline = translatedHeadline.length > 100
    ? `${translatedHeadline.substr(0, 97)}...`
    : article.headline;

  return (
    <div className={cx('article')}>
      {article.source && (
        <Header as="h3">
          {article.source}
        </Header>
      )}
      <Link
        className={cx('articleLink')}
        href={articleUrl}
        target={isExternalLink ? '_blank' : undefined}
        rel={isExternalLink ? 'noreferrer noopener' : undefined}
      >
        {formattedHeadline}
      </Link>
      <Text as="p" className={cx('articleDate')}>
        {formattedArticleDate}
      </Text>
    </div>
  );
}

ArticlePreview.propTypes = {
  article: PropTypes.shape({
    date: PropTypes.string.isRequired,
    headline: PropTypes.string.isRequired,
    headlineLink: PropTypes.string,
    locales: PropTypes.shape({
      fr: PropTypes.shape({
        headline: PropTypes.string,
      }),
    }),
    source: PropTypes.string,
  }),
  isExternalLink: PropTypes.bool,
};

ArticlePreview.defaultProps = {
  article: {},
  isExternalLink: false,
};

export default function NewsroomPage() {
  const { t, i18n } = useTranslation(['article', 'miscellaneous']);

  const currentYear = new Date(Date.now()).getFullYear();
  const { data, error, loading } = useQuery(GET_ARTICLES, {
    variables: {
      years: [currentYear, currentYear - 1],
    },
  });
  const inTheNewsArticles = data?.newsArticles?.slice(0, PAGE_LENGTH);
  const pressReleases = data?.pressReleases?.slice(0, PAGE_LENGTH);

  const noArticlesFound = !inTheNewsArticles?.length && !loading;
  const noReleasesFound = !pressReleases?.length && !loading;

  return (
    <Page
      compact
      name="Newsroom"
      title={t('newsroomMetaTitle', { ns: 'article' })}
      description={t('newsroomMetaDescription', { ns: 'article' })}
    >
      <Helmet>
        <meta property="og:image" content={PAGE_IMAGE} />
        <meta name="twitter:image" content={PAGE_IMAGE} />
      </Helmet>
      <GradientBanner className={cx('hero')}>
        <Container>
          <Header as="h1" color="white" elongated uppercase>
            {t('newsroom', { ns: 'article' })}
          </Header>
        </Container>
      </GradientBanner>
      <Container>
        <Text as="p" className={cx('heroText')}>
          <Trans i18nKey="article:newsroomHeroText">
            Contact us at <a href="mailto:press@gasbuddy.com">press@gasbuddy.com</a>
            <br />
            Need insights about gas prices? Get in touch with <a href="//fuelinsights.gasbuddy.com">our analysts</a>.
          </Trans>
        </Text>
        {!error && !loading && !!pressReleases?.length && i18n.language === SUPPORTED_LANGUAGES.FRENCH && (
          <Message className={cx('missingTranslations')} color="gold" icon={<FontAwesomeIcon icon={faExclamation} />}>
            {t('missingTranslations', { ns: 'article' })}
          </Message>
        )}
        <FlexGrid>
          <FlexGrid.Column tablet={6}>
            <div className={cx('section')}>
              <Header as="h2">
                {t('pressReleases', { ns: 'article' })}
              </Header>
              {loading && (
                <Loader>
                  {t('loadingPressReleases', { ns: 'article' })}
                </Loader>
              )}
              {(!!error || noReleasesFound) && (
                <Text as="p">
                  {t('noPressReleases', { ns: 'article' })}
                </Text>
              )}
              {!error && !!pressReleases?.length && (
                <Fragment>
                  {pressReleases.map(article => (
                    <ArticlePreview key={article.headline} article={article} />
                  ))}
                  <Link className={cx('seeAllLink')} href="/newsroom/pressreleases">
                    <Text>
                      {t('actions.seeAll', { ns: 'miscellaneous' })}
                      <FontAwesomeIcon icon={faAngleRight} size="2x" />
                    </Text>
                  </Link>
                </Fragment>
              )}
            </div>
          </FlexGrid.Column>
          <FlexGrid.Column tablet={6}>
            <div className={cx('section')}>
              <Header as="h2">
                {t('gasBuddyInTheNews', { ns: 'article' })}
              </Header>
              {loading && (
                <Loader>
                  {t('loadingArticles', { ns: 'article' })}
                </Loader>
              )}
              {(!!error || noArticlesFound) && (
                <Text as="p">
                  {t('noArticles', { ns: 'article' })}
                </Text>
              )}
              {!error && !!inTheNewsArticles?.length && (
                <Fragment>
                  {inTheNewsArticles.map(article => (
                    <ArticlePreview key={article.headline} article={article} isExternalLink />
                  ))}
                  <Link className={cx('seeAllLink')} href="/newsroom/inthenews">
                    <Text>
                      {t('actions.seeAll', { ns: 'miscellaneous' })}
                      <FontAwesomeIcon icon={faAngleRight} size="2x" />
                    </Text>
                  </Link>
                </Fragment>
              )}
            </div>
          </FlexGrid.Column>
        </FlexGrid>
        <div className={cx('section')}>
          <Header as="h2">
            {t('pressKit', { ns: 'article' })}
          </Header>
          <Text as="p">
            {t('pressKitDescription', { ns: 'article' })}
          </Text>
          <br />
          <Picture
            alt={t('pressKitAlt', { ns: 'article' })}
            srcSet={`
              https://static.gasbuddy.com/web/consumer/newsroom/newsroom-press-kit@2x.png 2x,
              https://static.gasbuddy.com/web/consumer/newsroom/newsroom-press-kit@3x.png 3x
            `}
            src="https://static.gasbuddy.com/web/consumer/newsroom/newsroom-press-kit.png"
          >
            <Picture.Source
              srcSet={`
                https://static.gasbuddy.com/web/consumer/newsroom/newsroom-press-kit.webp,
                https://static.gasbuddy.com/web/consumer/newsroom/newsroom-press-kit@2x.webp 2x,
                https://static.gasbuddy.com/web/consumer/newsroom/newsroom-press-kit@3x.webp 3x
              `}
              type="image/webp"
            />
          </Picture>
          <br />
          <br />
          <Button
            as="a"
            href="https://gasbuddy.invisionapp.com/board/GasBuddy-ckf8wqps8029m18wcl0xlligw"
            secondary
            target="_blank"
          >
            {t('downloadAssets', { ns: 'article' })}
          </Button>
        </div>
      </Container>
    </Page>
  );
}
